import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const CheckoutPage = () => {
  const { cart, fetchCart } = useCart();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [shippingInfo, setShippingInfo] = useState(null);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCard, setGiftCard] = useState(null);
  const [validatingGiftCard, setValidatingGiftCard] = useState(false);
  const [address, setAddress] = useState({
    name: user?.name || '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });

  const subtotal = cart?.subtotal ?? cart?.total ?? 0;
  const promoDiscount = cart?.discount || 0;
  const promoPercent = cart?.promo_percent || 0;
  const wholesalePercent = cart?.wholesale_percent || 0;
  const effectivePercent = cart?.effective_discount_percent || promoPercent;
  const isWholesale = !!cart?.is_wholesale;
  const storeCreditBalance = cart?.store_credit_balance || 0;
  const [useStoreCredit, setUseStoreCredit] = useState(false);
  const discountedSubtotal = subtotal - promoDiscount;
  const tax = discountedSubtotal * 0.07;

  useEffect(() => {
    const fetchShipping = async () => {
      if (!token || !cart?.items?.length) return;
      try {
        const response = await axios.post(`${API}/shipping/estimate`, { shipping_address: address }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setShippingInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch shipping estimate');
      }
    };
    if (address.state) fetchShipping();
  }, [address.state, token, cart]);

  const shipping = shippingInfo?.shipping || (discountedSubtotal >= 100 ? 0 : 14.05);
  const preCreditTotal = discountedSubtotal + tax + shipping;
  const creditApplied = useStoreCredit ? Math.min(storeCreditBalance, preCreditTotal) : 0;
  const total = Math.max(preCreditTotal - creditApplied, 0);

  const handleStripeCheckout = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/stripe`, {
        origin_url: window.location.origin,
        shipping_address: address,
        apply_store_credit: useStoreCredit,
        gift_card_code: giftCard ? giftCard.code : undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const handlePayPalCreateOrder = async () => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        shipping_address: address,
        origin_url: window.location.origin,
        apply_store_credit: useStoreCredit,
      }, { headers: { Authorization: `Bearer ${token}` } });
      return response.data.order_id;
    } catch (error) {
      toast.error('Failed to create PayPal order');
      throw error;
    }
  };

  const handlePayPalApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID,
        apply_store_credit: useStoreCredit,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchCart();
      toast.success('Payment successful!');
      window.location.href = '/checkout/success?paypal=true';
    } catch (error) {
      toast.error('Payment capture failed. Please contact support.');
    }
  };

  const handleInvoiceCheckout = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/checkout/wholesale-invoice`, {
        shipping_address: address,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchCart();
      toast.success(`Invoice created. Total $${r.data.total.toFixed(2)} due ${r.data.payment_due_at.slice(0, 10)}.`);
      window.location.href = '/orders';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
      setLoading(false);
    }
  };

  if (!cart?.items?.length) return (
    <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
      <p className="text-gray-400">Your cart is empty</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-5xl tracking-wide mb-8">CHECKOUT</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="checkout-form">
          <div className="space-y-6">
            <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
              <h3 className="font-heading text-xl mb-4">SHIPPING ADDRESS</h3>
              <div className="space-y-4">
                <input type="text" placeholder="Full Name" value={address.name} onChange={(e) => setAddress({...address, name: e.target.value})} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="checkout-name" />
                <input type="text" placeholder="Street Address" value={address.street} onChange={(e) => setAddress({...address, street: e.target.value})} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="checkout-street" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="City" value={address.city} onChange={(e) => setAddress({...address, city: e.target.value})} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="checkout-city" />
                  <input type="text" placeholder="State" value={address.state} onChange={(e) => setAddress({...address, state: e.target.value})} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="checkout-state" />
                </div>
                <input type="text" placeholder="ZIP Code" value={address.zip} onChange={(e) => setAddress({...address, zip: e.target.value})} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4" data-testid="checkout-zip" />
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
              <h3 className="font-heading text-xl mb-4">PAYMENT METHOD</h3>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={`flex-1 min-w-[120px] py-3 rounded-sm border-2 font-semibold text-sm transition-all ${paymentMethod === 'stripe' ? 'border-[#D62828] bg-[#D62828]/10 text-white' : 'border-white/10 text-gray-400 hover:border-white/30'}`}
                  data-testid="payment-stripe"
                >
                  Credit Card
                </button>
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`flex-1 min-w-[120px] py-3 rounded-sm border-2 font-semibold text-sm transition-all ${paymentMethod === 'paypal' ? 'border-[#003087] bg-[#003087]/10 text-[#009cde]' : 'border-white/10 text-gray-400 hover:border-white/30'}`}
                  data-testid="payment-paypal"
                >
                  PayPal
                </button>
                {isWholesale && (
                  <button
                    onClick={() => setPaymentMethod('invoice')}
                    className={`flex-1 min-w-[120px] py-3 rounded-sm border-2 font-semibold text-sm transition-all ${paymentMethod === 'invoice' ? 'border-yellow-500 bg-yellow-500/10 text-yellow-300' : 'border-white/10 text-gray-400 hover:border-white/30'}`}
                    data-testid="payment-invoice"
                  >
                    NET-30 Invoice
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-[#121212] border border-white/5 rounded-sm p-6 h-fit">
            <h3 className="font-heading text-xl mb-4">ORDER SUMMARY</h3>
            <div className="space-y-3 mb-6">
              {cart.items.map((item) => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span className="text-gray-400">{item.name} x{item.quantity}</span>
                  <span>${item.subtotal.toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-4 space-y-2">
              <div className="flex justify-between text-gray-400 text-sm"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              {promoDiscount > 0 && (
                <div className="flex justify-between text-green-400 text-sm font-semibold" data-testid="checkout-promo-line">
                  <span>{wholesalePercent > promoPercent ? `Wholesale Tier ${effectivePercent}% Off` : `Grand Opening ${effectivePercent}% Off`}</span>
                  <span>-${promoDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-400 text-sm"><span>Tax</span><span>${tax.toFixed(2)}</span></div>
              <div className="flex justify-between text-gray-400 text-sm">
                <span>Shipping (FedEx Ground)</span>
                <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
              </div>
              {storeCreditBalance > 0 && (
                <div className="bg-[#0F0F0F] border border-green-500/20 rounded-sm p-3 my-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="checkout-store-credit-toggle-label">
                    <input
                      type="checkbox"
                      checked={useStoreCredit}
                      onChange={(e) => setUseStoreCredit(e.target.checked)}
                      className="w-4 h-4 accent-green-500"
                      data-testid="checkout-store-credit-toggle"
                    />
                    <span className="text-white">Apply store credit</span>
                    <span className="ml-auto text-green-400 font-semibold">${storeCreditBalance.toFixed(2)} available</span>
                  </label>
                  {useStoreCredit && creditApplied > 0 && (
                    <p className="text-xs text-green-400 mt-1" data-testid="checkout-store-credit-applied">-${creditApplied.toFixed(2)} applied to this order</p>
                  )}
                </div>
              )}
              <div className="bg-[#0F0F0F] border border-[#D4AF37]/20 rounded-sm p-3 my-2" data-testid="gift-card-section">
                {giftCard ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#D4AF37] text-sm font-semibold">Gift card applied</p>
                      <p className="text-gray-400 text-xs font-mono">{giftCard.code}</p>
                      <p className="text-green-400 text-xs">-${Math.min(giftCard.balance, Math.max(preCreditTotal - creditApplied, 0)).toFixed(2)} applied</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setGiftCard(null); setGiftCardCode(''); }}
                      className="text-xs text-gray-400 hover:text-red-400 uppercase tracking-wide"
                      data-testid="remove-gift-card-btn"
                    >Remove</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={giftCardCode}
                      onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                      placeholder="Gift card code (CUTA-XXXX-XXXX-XXXX)"
                      className="flex-1 bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-2 text-xs text-white font-mono focus:border-[#D4AF37] focus:outline-none"
                      data-testid="gift-card-input"
                    />
                    <button
                      type="button"
                      disabled={validatingGiftCard || !giftCardCode.trim()}
                      onClick={async () => {
                        setValidatingGiftCard(true);
                        try {
                          const r = await axios.get(`${API}/gift-cards/validate/${giftCardCode.trim()}`);
                          setGiftCard(r.data);
                          toast.success(`Gift card applied: $${r.data.balance.toFixed(2)} balance`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Invalid gift card');
                        } finally {
                          setValidatingGiftCard(false);
                        }
                      }}
                      className="bg-[#D4AF37] hover:bg-[#C29F2A] text-black text-xs uppercase tracking-wide font-bold px-3 py-2 rounded-sm disabled:opacity-50"
                      data-testid="apply-gift-card-btn"
                    >
                      {validatingGiftCard ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>
              {shippingInfo && shippingInfo.amount_until_free_shipping > 0 && (
                <p className="text-xs text-green-500">Add ${shippingInfo.amount_until_free_shipping.toFixed(2)} more for free shipping!</p>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10">
                <span>Total</span><span className="text-[#D62828]">${total.toFixed(2)}</span>
              </div>
            </div>

            {paymentMethod === 'stripe' ? (
              <>
                <button
                  onClick={handleStripeCheckout}
                  disabled={loading}
                  className="w-full mt-6 bg-[#D62828] hover:bg-[#B91C1C] text-white py-4 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow disabled:opacity-50"
                  data-testid="pay-btn"
                >
                  {loading ? 'Processing...' : 'Pay with Stripe'}
                </button>
                <p className="text-center text-gray-500 text-xs mt-4 flex items-center justify-center gap-2">
                  <Lock className="w-3 h-3" />Secure checkout powered by Stripe
                </p>
              </>
            ) : paymentMethod === 'paypal' ? (
              <div className="mt-6">
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "blue", shape: "rect" }}
                    createOrder={handlePayPalCreateOrder}
                    onApprove={handlePayPalApprove}
                    onError={(err) => toast.error('PayPal error. Please try again.')}
                  />
                </PayPalScriptProvider>
                <p className="text-center text-gray-500 text-xs mt-4 flex items-center justify-center gap-2">
                  <Lock className="w-3 h-3" />Secure checkout powered by PayPal
                </p>
              </div>
            ) : (
              <>
                <button
                  onClick={handleInvoiceCheckout}
                  disabled={loading}
                  className="w-full mt-6 bg-yellow-600 hover:bg-yellow-700 text-white py-4 rounded-sm font-bold uppercase tracking-wider transition-all disabled:opacity-50"
                  data-testid="pay-invoice-btn"
                >
                  {loading ? 'Submitting...' : 'Place NET-30 Invoice Order'}
                </button>
                <p className="text-center text-gray-500 text-xs mt-4">
                  Your wholesale order will ship and we'll email a formal invoice. Payment due within 30 days.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
